import { useEffect, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Link,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import { DesktopActionCell, DesktopActionHeaderCell } from '../admin/DesktopTableActions';
import { formatApiDateTime } from '../admin/dateTimeUtils';
import {
    approvePendingUserPhoto,
    getPendingUserPhotos,
    rejectPendingUserPhoto,
    type PendingUserPhotoRecord,
} from '../admin/adminUserPhotoApi';

type ModerationAction = 'approve' | 'reject';

function formatDateTime(value: string): string {
    return formatApiDateTime(value);
}

function getDisplayTitle(record: PendingUserPhotoRecord): string {
    return record.displayName || record.email || record.userId;
}

function getInitials(record: PendingUserPhotoRecord): string {
    const source = getDisplayTitle(record)
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

    return source || 'U';
}

type PendingPhotoPreviewProps = {
    src: string;
    alt: string;
    initials: string;
};

function PendingPhotoPreview({ src, alt, initials }: PendingPhotoPreviewProps) {
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        setHasError(false);
    }, [src]);

    if (!src || hasError) {
        return (
            <Avatar variant="rounded" sx={{ width: 72, height: 72 }}>
                {initials}
            </Avatar>
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setHasError(true)}
            sx={{
                width: 72,
                height: 72,
                display: 'block',
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.100',
            }}
        />
    );
}

export function UserPhotoApprovalPage() {
    const notify = useNotify();
    const [searchDraft, setSearchDraft] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [rows, setRows] = useState<PendingUserPhotoRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [activeActionKey, setActiveActionKey] = useState('');
    const [settledRequestKey, setSettledRequestKey] = useState('');

    const requestKey = `${page}:${pageSize}:${search}:${refreshNonce}`;
    const isLoading = settledRequestKey !== requestKey;

    useEffect(() => {
        let isActive = true;

        async function loadPendingPhotos() {
            try {
                const result = await getPendingUserPhotos({
                    page: page + 1,
                    pageSize,
                    search,
                });

                if (!isActive) {
                    return;
                }

                setRows(result.data);
                setTotal(result.total);
                setErrorMessage('');
                setSettledRequestKey(requestKey);
            } catch (error) {
                if (!isActive) {
                    return;
                }

                const message = error instanceof Error ? error.message : 'Unable to load pending profile photos';
                setRows([]);
                setTotal(0);
                setErrorMessage(message);
                setSettledRequestKey(requestKey);
            }
        }

        void loadPendingPhotos();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, requestKey, search]);

    async function handleModerationAction(action: ModerationAction, userId: string) {
        const actionLabel = action === 'approve' ? 'approve' : 'reject';
        const actionKey = `${action}:${userId}`;

        setActiveActionKey(actionKey);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const result =
                action === 'approve'
                    ? await approvePendingUserPhoto(userId)
                    : await rejectPendingUserPhoto(userId);

            setSuccessMessage(result.message);
            notify(result.message, { type: 'info' });

            if (rows.length === 1 && page > 0) {
                setPage((currentPage) => currentPage - 1);
            } else {
                setRefreshNonce((value) => value + 1);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : `Unable to ${actionLabel} the pending profile photo`;
            setErrorMessage(message);
            notify(message, { type: 'error' });
        } finally {
            setActiveActionKey('');
        }
    }

    function handleSearch(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setPage(0);
        setSuccessMessage('');
        setErrorMessage('');
        setSearch(searchDraft.trim());
    }

    function handleClearSearch() {
        setSearchDraft('');
        setSearch('');
        setPage(0);
        setSuccessMessage('');
        setErrorMessage('');
    }

    return (
        <Card>
            <Title title="Photo Approval" />
            {isLoading ? <LinearProgress /> : null}
            <CardContent>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography variant="h4">Photo Approval</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review pending user profile photos, search by user details, and approve or reject them from one moderation queue.
                        </Typography>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack
                            component="form"
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            onSubmit={handleSearch}
                            alignItems={{ md: 'center' }}
                        >
                            <TextField
                                label="Search Pending Photos"
                                value={searchDraft}
                                onChange={(event) => setSearchDraft(event.target.value)}
                                placeholder="Search by user ID, email, or name"
                                fullWidth
                            />
                            <Stack direction="row" spacing={1}>
                                <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
                                    Search
                                </Button>
                                <Button variant="outlined" onClick={handleClearSearch} disabled={!searchDraft && !search}>
                                    Clear
                                </Button>
                                <IconButton
                                    aria-label="Refresh pending photos"
                                    onClick={() => setRefreshNonce((value) => value + 1)}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                            <Typography variant="overline" color="text.secondary">
                                Total Pending
                            </Typography>
                            <Typography variant="h5">{total}</Typography>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, minWidth: 180 }}>
                            <Typography variant="overline" color="text.secondary">
                                Current Search
                            </Typography>
                            <Typography variant="body1">{search || 'All pending photos'}</Typography>
                        </Paper>
                    </Stack>

                    {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
                    {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

                    <Stack spacing={1.5} sx={{ display: { xs: 'flex', lg: 'none' } }}>
                        {rows.map((record) => {
                            const previewUrl = record.thumbnailUrl || record.pendingPhotoUrl;
                            const initials = getInitials(record);
                            const isApproving = activeActionKey === `approve:${record.userId}`;
                            const isRejecting = activeActionKey === `reject:${record.userId}`;

                            return (
                                <Card key={record.id} variant="outlined">
                                    <CardContent>
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                {previewUrl ? (
                                                    <PendingPhotoPreview
                                                        src={previewUrl}
                                                        alt={getDisplayTitle(record)}
                                                        initials={initials}
                                                    />
                                                ) : (
                                                    <Avatar variant="rounded" sx={{ width: 72, height: 72 }}>
                                                        {initials}
                                                    </Avatar>
                                                )}
                                                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                                    <Typography variant="subtitle2">{getDisplayTitle(record)}</Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                        {record.email || 'No email returned'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                        User ID: {record.userId}
                                                    </Typography>
                                                </Stack>
                                            </Stack>

                                            <Stack spacing={0.75}>
                                                <Typography variant="body2">Created: {formatDateTime(record.createdAt)}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Updated: {formatDateTime(record.updatedAt)}
                                                </Typography>
                                                {record.pendingPhotoUrl ? (
                                                    <Link href={record.pendingPhotoUrl} target="_blank" rel="noreferrer" underline="hover">
                                                        Open pending photo
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No pending photo URL returned
                                                    </Typography>
                                                )}
                                            </Stack>

                                            <Stack
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={1}
                                                useFlexGap
                                                flexWrap="wrap"
                                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                            >
                                                <Typography variant="body2" fontWeight={600} color="text.secondary">
                                                    Status: {record.status}
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    startIcon={
                                                        isApproving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />
                                                    }
                                                    disabled={Boolean(activeActionKey)}
                                                    onClick={() => void handleModerationAction('approve', record.userId)}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={
                                                        isRejecting ? <CircularProgress size={16} color="inherit" /> : <ThumbDownAltOutlinedIcon />
                                                    }
                                                    disabled={Boolean(activeActionKey)}
                                                    onClick={() => void handleModerationAction('reject', record.userId)}
                                                >
                                                    Reject
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}

                        {!rows.length && !isLoading ? (
                            <Card variant="outlined">
                                <CardContent>
                                    <Alert severity="info">
                                        {search
                                            ? 'No pending profile photos matched the current search.'
                                            : 'There are no pending profile photos to review right now.'}
                                    </Alert>
                                </CardContent>
                            </Card>
                        ) : null}
                    </Stack>

                    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', lg: 'block' } }}>
                        <Table sx={{ minWidth: 860 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Photo</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Submitted</TableCell>
                                    <TableCell>Source</TableCell>
                                    <DesktopActionHeaderCell>Approve</DesktopActionHeaderCell>
                                    <DesktopActionHeaderCell>Reject</DesktopActionHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((record) => {
                                    const previewUrl = record.thumbnailUrl || record.pendingPhotoUrl;
                                    const initials = getInitials(record);
                                    const isApproving = activeActionKey === `approve:${record.userId}`;
                                    const isRejecting = activeActionKey === `reject:${record.userId}`;

                                    return (
                                        <TableRow key={record.id} hover>
                                            <TableCell sx={{ width: 120 }}>
                                                {previewUrl ? (
                                                    <PendingPhotoPreview
                                                        src={previewUrl}
                                                        alt={getDisplayTitle(record)}
                                                        initials={initials}
                                                    />
                                                ) : (
                                                    <Avatar variant="rounded" sx={{ width: 72, height: 72 }}>
                                                        {initials}
                                                    </Avatar>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.75}>
                                                    <Typography variant="subtitle2">{getDisplayTitle(record)}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {record.email || 'No email returned'}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        User ID: {record.userId}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Status: {record.status}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.75}>
                                                    <Typography variant="body2">Created: {formatDateTime(record.createdAt)}</Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Updated: {formatDateTime(record.updatedAt)}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                {record.pendingPhotoUrl ? (
                                                    <Link href={record.pendingPhotoUrl} target="_blank" rel="noreferrer" underline="hover">
                                                        Open pending photo
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No pending photo URL returned
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <DesktopActionCell>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    startIcon={
                                                        isApproving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />
                                                    }
                                                    disabled={Boolean(activeActionKey)}
                                                    onClick={() => void handleModerationAction('approve', record.userId)}
                                                >
                                                    Approve
                                                </Button>
                                            </DesktopActionCell>
                                            <DesktopActionCell>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={
                                                        isRejecting ? <CircularProgress size={16} color="inherit" /> : <ThumbDownAltOutlinedIcon />
                                                    }
                                                    disabled={Boolean(activeActionKey)}
                                                    onClick={() => void handleModerationAction('reject', record.userId)}
                                                >
                                                    Reject
                                                </Button>
                                            </DesktopActionCell>
                                        </TableRow>
                                    );
                                })}

                                {!rows.length && !isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Alert severity="info">
                                                {search
                                                    ? 'No pending profile photos matched the current search.'
                                                    : 'There are no pending profile photos to review right now.'}
                                            </Alert>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={(_event, nextPage) => setPage(nextPage)}
                            onRowsPerPageChange={(event) => {
                                setPageSize(Number(event.target.value));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </TableContainer>
                </Stack>
            </CardContent>
        </Card>
    );
}